import { z } from 'zod/v4';

const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
  .optional();

const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

/**
 * Upper bound on the `filter` JSON. A multi-select filter is sent as every
 * selected value comma-joined, so "select all" in the facility-process picker
 * alone is ~900 characters — 512 rejected it, and frontend-v2 restores a
 * saved filter on every visit, which locked the user out of My Network.
 * Shared with the network graph, which takes the same filter.
 *
 * Not higher because the filter rides in the query string: Cloudflare and
 * Node each cap a request at ~16 KB, and non-Latin text (e.g. Chinese
 * company names in `officialName`) grows ~7x when percent-encoded.
 */
export const COMPANIES_FILTER_MAX_LENGTH = 2048;

export const CompaniesListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: sortParamSchema,
  filter: z.string().max(COMPANIES_FILTER_MAX_LENGTH).optional(),
  hasFeature: z.string().min(1).optional(),
  hasRelationshipType: booleanLike,
  isSupplierNetwork: booleanLike,
  companyIds: z.string().min(1).optional(),
  excludeCompanyIds: z.string().min(1).optional(),
  includeOwnCompany: booleanLike,
  findSimilar: booleanLike,
  name: z.string().min(1).optional(),
  isDropDown: booleanLike,
  hasGeojson: booleanLike,
});

export type CompaniesListQuery = z.infer<typeof CompaniesListQuerySchema>;

// `z.looseObject` because the SQL returns ~40 columns of varying runtime
// shapes (Date objects, bigint-as-string counts, nested JSON arrays) and
// adding strict typing here duplicates the row shape without buying
// validation strength — same pragmatic choice as `AdminCompanyListRowSchema`.
// Only the fields every consumer relies on are typed; everything else
// passes through untouched.
export const CompanyListRowSchema = z.looseObject({
  id: z.string(),
  name: z.string().nullable().optional(),
  officialName: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  internalCompanyCode: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});

export type CompanyListRow = z.infer<typeof CompanyListRowSchema>;

export const CompaniesListResponseSchema = z.object({
  data: z.array(CompanyListRowSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type CompaniesListResponse = z.infer<typeof CompaniesListResponseSchema>;
